import {
  Scene,
  Camera,
  PostProcess,
  Effect,
  VolumetricLightScatteringPostProcess,
  Texture,
  Mesh,
  ChromaticAberrationPostProcess,
  PBRMaterial,
  Material,
  LensRenderingPipeline,
  Vector3,
} from '@babylonjs/core';
import { logger } from '../core/Logger';

/** Configuration for lens rendering pipeline effects */
export interface LensEffectConfig {
  /** Edge blur amount (0 = none, 1 = realistic) */
  edge_blur: number;
  /** Chromatic aberration amount (0 = none, 1 = realistic) */
  chromatic_aberration: number;
  /** Lens distortion amount (0 = none, 1 = realistic) */
  distortion: number;
  /** Film grain amount (0 = none, 1 = heavy) */
  grain_amount: number;
  /** Depth of field focus distance */
  dof_focus_distance: number;
  /** DoF aperture - blur bias (higher = more blur) */
  dof_aperture: number;
  /** DoF darken out-of-focus areas (0-1) */
  dof_darken: number;
  /** Pentagon-shaped bokeh effect */
  dof_pentagon: boolean;
  /** DoF highlights gain (0 = disabled) */
  dof_gain: number;
  /** DoF highlights threshold */
  dof_threshold: number;
  /** Auto-focus on player (distance calculated automatically) */
  dof_auto_focus: boolean;
  /** Enable/disable depth of field effect */
  dof_enabled: boolean;
}

/** Detect mobile device */
function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
}

export class PostProcessManager {
  private scene: Scene;
  private camera: Camera;
  private pixelationPost: PostProcess | null = null;
  private pixelationAttached: boolean = false;
  private chromaticAberrationPost: ChromaticAberrationPostProcess | null = null;
  private chromaticAberrationAttached: boolean = false;
  private godRaysPosts: VolumetricLightScatteringPostProcess[] = [];
  private godRaysMeshes: string[] = [];
  // Map mesh name to its godrays post-process for per-mesh control
  private godRaysMeshToPost: Map<string, VolumetricLightScatteringPostProcess> = new Map();
  private enabled: boolean = false;
  private godRaysEnabled: boolean = false;
  private chromaticAberrationEnabled: boolean = false;
  private chromaticAberrationAmount: number = 12;
  private chromaticAberrationRadialIntensity: number = 1.0;
  public pixelateSizeX: number = 1;
  public pixelateSizeY: number = this.pixelateSizeX;
  
  // Lens rendering pipeline
  private lensRenderingPipeline: LensRenderingPipeline | null = null;
  private lensEffectEnabled: boolean = false;
  private lensConfig: LensEffectConfig = {
    edge_blur: 0.0,
    chromatic_aberration: 0.0,
    distortion: 0.0,
    grain_amount: 0.0,
    dof_focus_distance: 50,
    dof_aperture: 0.03,  // Lower = less blur (docs: high aperture = miniature effect = more blur)
    dof_darken: 0.0,
    dof_pentagon: true,
    dof_gain: 1.0,  // This is just for bokeh highlights, not DoF enable
    dof_threshold: 1.0,
    dof_auto_focus: true,
    dof_enabled: false,  // Separate flag to enable/disable DoF
  };
  
  // Auto-focus target position getter (set by main.ts)
  private autoFocusTargetGetter: (() => Vector3 | null) | null = null;

  constructor(scene: Scene, camera: Camera) {
    this.scene = scene;
    this.camera = camera;
    
    // Disable pixelation by default on mobile for performance
    if (isMobileDevice()) {
      this.enabled = false;
    }
    
    // Apply default state
    if (this.enabled) {
        this.togglePixelation(true);
    }
  }

  public initializePixelation(): void {
    Effect.ShadersStore["pixelationFragmentShader"] = `
      precision highp float;
      varying vec2 vUV;
      uniform sampler2D textureSampler;
      uniform vec2 screenSize;
      uniform float pixelateSizeX;
      uniform float pixelateSizeY;

      void main(void) {
        vec2 texSize = screenSize.xy;
        
        // Calculate pixelated coordinates
        float dx = pixelateSizeX / texSize.x;
        float dy = pixelateSizeY / texSize.y;
        
        vec2 coord = vec2(dx * floor(vUV.x / dx), dy * floor(vUV.y / dy));
        
        gl_FragColor = texture2D(textureSampler, coord);
      }
    `;

    this.pixelationPost = new PostProcess(
      "pixelation",
      "pixelation",
      ["screenSize", "pixelateSizeX", "pixelateSizeY"],
      null,
      1.0,
      null,
      undefined,
      this.scene.getEngine()
    );

    this.pixelationPost.samples = this.pixelateSizeX;

    this.pixelationPost.onApply = (effect) => {
      effect.setFloat2("screenSize", 
        this.pixelationPost!.width, 
        this.pixelationPost!.height
      );
      effect.setFloat("pixelateSizeX", this.pixelateSizeX);
      effect.setFloat("pixelateSizeY", this.pixelateSizeY);
    };

    logger.debug.log('✅ Pixelation post-process initialized');
  }

  public togglePixelation(enabled: boolean): void {
    if (!this.pixelationPost) {
      this.initializePixelation();
    }

    // Idempotent: avoid attaching the same instance twice.
    if (this.enabled === enabled) {
      if (enabled && this.pixelationAttached) return;
      if (!enabled && !this.pixelationAttached) return;
    }

    this.enabled = enabled;

    if (!this.pixelationPost) return;

    if (enabled) {
      if (!this.pixelationAttached) {
        this.camera.attachPostProcess(this.pixelationPost);
        this.pixelationAttached = true;
      }
      return;
    }

    if (this.pixelationAttached) {
      this.camera.detachPostProcess(this.pixelationPost);
      this.pixelationAttached = false;
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  private initializeChromaticAberration(): void {
    const engine = this.scene.getEngine();
    const width = engine.getRenderWidth(true);
    const height = engine.getRenderHeight(true);

    // Pass null camera; we manage attaching/detaching explicitly.
    this.chromaticAberrationPost = new ChromaticAberrationPostProcess(
      'chromaticAberration',
      width,
      height,
      1.0,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      engine,
      false
    );

    // Defaults are quite strong (aberrationAmount default: 30). Use our current configured values.
    this.chromaticAberrationPost.aberrationAmount = this.chromaticAberrationAmount;
    this.chromaticAberrationPost.radialIntensity = this.chromaticAberrationRadialIntensity;

    logger.debug.log('✅ Chromatic aberration post-process initialized');
  }

  public toggleChromaticAberration(enabled: boolean): void {
    if (!this.chromaticAberrationPost) {
      this.initializeChromaticAberration();
    }

    // Idempotent
    if (this.chromaticAberrationEnabled === enabled) {
      if (enabled && this.chromaticAberrationAttached) return;
      if (!enabled && !this.chromaticAberrationAttached) return;
    }

    this.chromaticAberrationEnabled = enabled;
    if (!this.chromaticAberrationPost) return;

    // Ensure screen size is current when enabling.
    if (enabled) {
      const engine = this.scene.getEngine();
      this.chromaticAberrationPost.screenWidth = engine.getRenderWidth(true);
      this.chromaticAberrationPost.screenHeight = engine.getRenderHeight(true);

      this.chromaticAberrationPost.aberrationAmount = this.chromaticAberrationAmount;
      this.chromaticAberrationPost.radialIntensity = this.chromaticAberrationRadialIntensity;

      if (!this.chromaticAberrationAttached) {
        this.camera.attachPostProcess(this.chromaticAberrationPost);
        this.chromaticAberrationAttached = true;
      }
      this.reorderPostProcesses();
      return;
    }

    if (this.chromaticAberrationAttached) {
      this.camera.detachPostProcess(this.chromaticAberrationPost);
      this.chromaticAberrationAttached = false;
    }
  }

  public isChromaticAberrationEnabled(): boolean {
    return this.chromaticAberrationEnabled;
  }

  public setChromaticAberrationAmount(value: number): void {
    const clamped = Math.max(0, Math.min(50, value));
    this.chromaticAberrationAmount = clamped;
    if (this.chromaticAberrationPost) {
      this.chromaticAberrationPost.aberrationAmount = clamped;
    }
  }

  public setChromaticAberrationRadialIntensity(value: number): void {
    const clamped = Math.max(0, Math.min(5, value));
    this.chromaticAberrationRadialIntensity = clamped;
    if (this.chromaticAberrationPost) {
      this.chromaticAberrationPost.radialIntensity = clamped;
    }
  }

  public detectAndInitializeGodRays(): void {
    // Find all meshes with materials that have hm_materialGodrays_enabled = true
    // This follows the MATERIAL_EFFECT_SCHEMAS pattern from BehaviorSchema.ts
    const godRaysMeshes = this.scene.meshes.filter(mesh => {
      // Check material extras for godrays enabled
      const matExtras = (mesh.material as any)?.metadata?.gltf?.extras;
      if (matExtras && matExtras.hm_materialGodrays_enabled) {
        return true;
      }
      return false;
    });
    
    if (godRaysMeshes.length > 0) {
      logger.debug.log(`✅ Found ${godRaysMeshes.length} god rays mesh(es):`, godRaysMeshes.map(m => m.name).join(', '));
      
      // Create a god rays post process for each mesh
      godRaysMeshes.forEach((mesh, _index) => {
        // VolumetricLightScatteringPostProcess requires a Mesh, not AbstractMesh
        if (!(mesh instanceof Mesh)) {
          logger.debug.warn(`⚠️ God rays mesh ${mesh.name} is not a Mesh, skipping`);
          return;
        }
        
        // Mark this mesh as a god rays emitter
        mesh.metadata = mesh.metadata || {};
        mesh.metadata.godraysEmitter = true;
        
        // Read material effect parameters from material extras (hm_materialGodrays_* prefix)
        const matExtras = (mesh.material as any)?.metadata?.gltf?.extras || {};
        const exposure = matExtras.hm_materialGodrays_exposure ?? 0.3;
        const decay = matExtras.hm_materialGodrays_decay ?? 0.97;
        const weight = matExtras.hm_materialGodrays_weight ?? 0.59;
        const density = matExtras.hm_materialGodrays_density ?? 0.93;
        
        const postProcess = new VolumetricLightScatteringPostProcess(
          `godrays_${mesh.name}`,
          1.0,
          this.camera,
          mesh as Mesh,
          100,
          Texture.BILINEAR_SAMPLINGMODE,
          this.scene.getEngine(),
          false
        );
        
        // excludedMeshes = meshes that do NOT appear in the occlusion pass
        // We want all scene meshes to appear as BLACK occluders (blocking the light)
        // EXCEPT the godrays emitter itself which should appear as the bright light source
        // 
        // For meshes with alpha-test materials (trees, foliage, etc.), Babylon.js automatically
        // uses the alpha texture to determine which pixels should be discarded in the occlusion pass.
        // This allows light rays to pass through transparent parts of the texture.
        //
        // DO NOT exclude meshes here - that would prevent them from occluding the light!
        
        // Configure PBR materials with alpha textures to use alpha testing for the occlusion pass
        // Without this, glTF materials with alpha blending won't report needAlphaTestingForMesh()
        // and the volumetric light scattering pass will treat them as solid geometry
        this.configureMaterialsForAlphaTesting();
        
        // Configure god rays from material parameters
        postProcess.exposure = exposure;
        postProcess.decay = decay;
        postProcess.weight = weight;
        postProcess.density = density;
        
        this.godRaysPosts.push(postProcess);
        this.godRaysMeshes.push(mesh.name);
        // Store mapping for per-mesh control
        this.godRaysMeshToPost.set(mesh.name, postProcess);
      });
      
      // Enable by default if meshes found
      this.godRaysEnabled = true;
      logger.debug.debug('✅ God rays enabled by default');
      
      // Re-order post-processes: pixelation must come last
      this.reorderPostProcesses();
    } else {
      logger.debug.debug('ℹ️ No god rays meshes found (looking for hm_materialGodrays_enabled on materials)');
    }
  }

  /**
   * Configure PBR materials with alpha textures to use alpha testing mode.
   * This is required for VolumetricLightScatteringPostProcess to properly respect
   * alpha channels in materials (e.g., tree leaves, foliage).
   * 
   * Without this, glTF materials using alpha blending will be treated as solid
   * geometry in the occlusion pass, blocking all light rays.
   */
  private configureMaterialsForAlphaTesting(): void {
    let configuredCount = 0;
    
    for (const material of this.scene.materials) {
      if (material instanceof PBRMaterial) {
        const pbrMat = material as PBRMaterial;
        
        // Check if this material has an albedo texture with alpha channel
        const albedoTexture = pbrMat.albedoTexture;
        if (albedoTexture && albedoTexture.hasAlpha) {
          // Enable alpha from albedo texture - this makes needAlphaTestingForMesh() return true
          // and getAlphaTestTexture() return the albedo texture
          pbrMat.useAlphaFromAlbedoTexture = true;
          
          // Set transparency mode to alpha test (cutout) instead of alpha blend
          // This is more performant and works correctly with the godrays occlusion pass
          // Alpha test uses a threshold (default 0.4 in the shader) - pixels below are discarded
          pbrMat.transparencyMode = Material.MATERIAL_ALPHATEST;
          
          configuredCount++;
        }
      }
    }
    
    if (configuredCount > 0) {
      logger.debug.log(`☀️ Configured ${configuredCount} material(s) with alpha for godrays occlusion`);
    }
  }

  private reorderPostProcesses(): void {
    // Ensure pixelation is always last in the chain
    if (this.pixelationPost && this.enabled && this.pixelationAttached) {
      this.camera.detachPostProcess(this.pixelationPost);
      this.camera.attachPostProcess(this.pixelationPost);
      logger.debug.debug('🔄 Reordered post-processes: pixelation now last in chain');
    }
  }

  public toggleGodRays(enabled: boolean): void {
    if (this.godRaysMeshes.length === 0) {
      if (enabled) {
        logger.debug.warn('⚠️ No god rays meshes found. Set hm_materialGodrays_enabled=true on a material in Blender.');
      }
      return;
    }
    
    this.godRaysEnabled = enabled;
    
    // Toggle visibility of all god rays meshes
    this.godRaysMeshes.forEach(meshName => {
      const mesh = this.scene.getMeshByName(meshName);
      if (mesh) {
        mesh.isVisible = enabled;
      }
    });
    
    logger.debug.debug(`☀️ God rays ${enabled ? 'enabled' : 'disabled'} for ${this.godRaysMeshes.length} mesh(es)`);
  }

  public isGodRaysEnabled(): boolean {
    return this.godRaysEnabled;
  }

  public hasGodRaysMeshes(): boolean {
    return this.godRaysMeshes.length > 0;
  }

  /**
   * Check if a specific mesh has godrays enabled
   */
  public meshHasGodrays(meshName: string): boolean {
    return this.godRaysMeshToPost.has(meshName);
  }

  /**
   * Get godrays params for a specific mesh
   */
  public getMeshGodRaysParams(meshName: string): { exposure: number; decay: number; weight: number; density: number } | null {
    const post = this.godRaysMeshToPost.get(meshName);
    if (!post) return null;
    return {
      exposure: post.exposure,
      decay: post.decay,
      weight: post.weight,
      density: post.density,
    };
  }

  /**
   * Set godrays exposure for a specific mesh
   */
  public setMeshGodRaysExposure(meshName: string, value: number): void {
    const post = this.godRaysMeshToPost.get(meshName);
    if (!post) return;
    post.exposure = Math.max(0, Math.min(2, value));
  }

  /**
   * Set godrays decay for a specific mesh
   */
  public setMeshGodRaysDecay(meshName: string, value: number): void {
    const post = this.godRaysMeshToPost.get(meshName);
    if (!post) return;
    post.decay = Math.max(0.8, Math.min(1, value));
  }

  /**
   * Set godrays weight for a specific mesh
   */
  public setMeshGodRaysWeight(meshName: string, value: number): void {
    const post = this.godRaysMeshToPost.get(meshName);
    if (!post) return;
    post.weight = Math.max(0, Math.min(2, value));
  }

  /**
   * Set godrays density for a specific mesh
   */
  public setMeshGodRaysDensity(meshName: string, value: number): void {
    const post = this.godRaysMeshToPost.get(meshName);
    if (!post) return;
    post.density = Math.max(0, Math.min(1, value));
  }

  /**
   * Enable a mesh to emit god rays. Call this to make a mesh contribute to the god rays effect.
   * Use this from behavior hooks or scripts to dynamically enable god rays on meshes.
   * 
   * @param mesh The mesh to enable as a god rays emitter
   */
  public enableMeshGodrays(mesh: Mesh): void {
    mesh.metadata = mesh.metadata || {};
    mesh.metadata.godraysEmitter = true;
    
    // Remove from excluded lists of all god rays post processes
    for (const post of this.godRaysPosts) {
      const idx = post.excludedMeshes.indexOf(mesh);
      if (idx >= 0) {
        post.excludedMeshes.splice(idx, 1);
      }
    }
    
    console.log(`☀️ Enabled god rays emission for mesh: ${mesh.name}`);
  }

  /**
   * Disable a mesh from emitting god rays.
   * Use this from behavior hooks or scripts to dynamically disable god rays on meshes.
   * 
   * @param mesh The mesh to disable as a god rays emitter
   */
  public disableMeshGodrays(mesh: Mesh): void {
    if (mesh.metadata) {
      mesh.metadata.godraysEmitter = false;
    }
    
    // Add to excluded lists of all god rays post processes
    for (const post of this.godRaysPosts) {
      if (!post.excludedMeshes.includes(mesh)) {
        post.excludedMeshes.push(mesh);
      }
    }
    
    console.log(`☀️ Disabled god rays emission for mesh: ${mesh.name}`);
  }

  /**
   * Check if a mesh is currently emitting god rays.
   */
  public isMeshGodraysEnabled(mesh: Mesh): boolean {
    return mesh.metadata?.godraysEmitter === true;
  }

  /**
   * Clear all god rays effects (call on level unload).
   * This removes references to disposed meshes.
   */
  public clearGodRays(): void {
    this.godRaysPosts.forEach(post => {
      post.dispose(this.camera);
    });
    this.godRaysPosts = [];
    this.godRaysMeshes = [];
    this.godRaysMeshToPost.clear();
    this.godRaysEnabled = false;
    logger.level.log('🌟 God rays cleared');
  }

  // ==================== Lens Effect Methods ====================
  
  /**
   * Initialize and enable the lens rendering pipeline with the given config.
   * The lens effect provides DoF, chromatic aberration, edge blur, distortion, and grain.
   */
  public initializeLensEffect(config?: Partial<LensEffectConfig>): void {
    // Dispose existing pipeline if any
    if (this.lensRenderingPipeline) {
      this.lensRenderingPipeline.dispose();
      this.lensRenderingPipeline = null;
    }
    
    // Merge config with defaults
    if (config) {
      this.lensConfig = { ...this.lensConfig, ...config };
    }
    
    // Build parameters object for LensRenderingPipeline
    const params: Record<string, unknown> = {
      edge_blur: this.lensConfig.edge_blur,
      chromatic_aberration: this.lensConfig.chromatic_aberration,
      distortion: this.lensConfig.distortion,
      grain_amount: this.lensConfig.grain_amount,
    };
    
    // Include DoF params if enabled
    // Note: dof_aperture controls blur amount - HIGHER = MORE blur (miniature effect)
    // dof_gain controls bokeh highlight intensity, not DoF enable/disable
    if (this.lensConfig.dof_enabled) {
      params.dof_focus_distance = this.lensConfig.dof_focus_distance;
      params.dof_aperture = this.lensConfig.dof_aperture;  // Low values = subtle blur
      params.dof_darken = 0;  // Never darken - causes black backgrounds
      params.dof_pentagon = this.lensConfig.dof_pentagon;
      params.dof_gain = this.lensConfig.dof_gain;  // Bokeh highlight intensity
      params.dof_threshold = this.lensConfig.dof_threshold;
    }
    
    this.lensRenderingPipeline = new LensRenderingPipeline(
      'lensEffect',
      params,
      this.scene,
      1.0,
      [this.camera]
    );
    
    // Explicitly disable DoF if not enabled
    if (!this.lensConfig.dof_enabled) {
      this.lensRenderingPipeline.disableDepthOfField();
      logger.debug.log('🔭 DoF disabled at init');
    }
    
    this.lensEffectEnabled = true;
    logger.debug.log('✅ Lens rendering pipeline initialized', params);
    
    // Reorder post-processes
    this.reorderPostProcesses();
  }
  
  /**
   * Toggle the lens effect on/off
   */
  public toggleLensEffect(enabled: boolean): void {
    if (enabled && !this.lensRenderingPipeline) {
      this.initializeLensEffect();
      return;
    }
    
    if (!this.lensRenderingPipeline) return;
    
    this.lensEffectEnabled = enabled;
    
    if (enabled) {
      // Re-attach camera to pipeline
      this.scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline('lensEffect', this.camera);
    } else {
      // Detach camera from pipeline
      this.scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline('lensEffect', this.camera);
    }
    
    logger.debug.log(`🔭 Lens effect ${enabled ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Check if lens effect is enabled
   */
  public isLensEffectEnabled(): boolean {
    return this.lensEffectEnabled;
  }
  
  /**
   * Get current lens effect config
   */
  public getLensConfig(): LensEffectConfig {
    return { ...this.lensConfig };
  }
  
  /**
   * Update lens effect parameters at runtime
   */
  public setLensConfig(config: Partial<LensEffectConfig>): void {
    this.lensConfig = { ...this.lensConfig, ...config };
    
    if (this.lensRenderingPipeline) {
      // Update individual properties
      if (config.edge_blur !== undefined) {
        this.lensRenderingPipeline.setEdgeBlur(config.edge_blur);
      }
      if (config.chromatic_aberration !== undefined) {
        this.lensRenderingPipeline.setChromaticAberration(config.chromatic_aberration);
      }
      if (config.distortion !== undefined) {
        this.lensRenderingPipeline.setEdgeDistortion(config.distortion);
      }
      if (config.grain_amount !== undefined) {
        this.lensRenderingPipeline.setGrainAmount(config.grain_amount);
      }
      
      // Handle DOF enabled/disabled toggle
      if (config.dof_enabled !== undefined) {
        if (!config.dof_enabled) {
          this.lensRenderingPipeline.disableDepthOfField();
          logger.debug.log('🔭 DoF disabled');
        }
        // If enabling, we need to set all params
      }
      
      // Only apply DOF params if enabled
      if (this.lensConfig.dof_enabled) {
        if (config.dof_focus_distance !== undefined) {
          this.lensRenderingPipeline.setFocusDistance(config.dof_focus_distance);
        }
        if (config.dof_aperture !== undefined) {
          this.lensRenderingPipeline.setAperture(config.dof_aperture);
        }
        if (config.dof_gain !== undefined) {
          this.lensRenderingPipeline.setHighlightsGain(config.dof_gain);
        }
        if (config.dof_threshold !== undefined) {
          this.lensRenderingPipeline.setHighlightsThreshold(config.dof_threshold);
        }
        // Never darken - causes black backgrounds
        this.lensRenderingPipeline.setDarkenOutOfFocus(0);
      }
      // Note: dof_pentagon requires pipeline recreation
    }
  }
  
  /**
   * Enable/disable DoF
   */
  public setDofEnabled(enabled: boolean): void {
    this.lensConfig.dof_enabled = enabled;
    if (this.lensRenderingPipeline) {
      if (!enabled) {
        this.lensRenderingPipeline.disableDepthOfField();
      } else {
        // Re-apply DoF settings
        this.lensRenderingPipeline.setFocusDistance(this.lensConfig.dof_focus_distance);
        this.lensRenderingPipeline.setAperture(this.lensConfig.dof_aperture);
        this.lensRenderingPipeline.setHighlightsGain(this.lensConfig.dof_gain);
        this.lensRenderingPipeline.setDarkenOutOfFocus(0);
      }
    }
    logger.debug.log(`🔭 DoF ${enabled ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Check if DoF is enabled
   */
  public isDofEnabled(): boolean {
    return this.lensConfig.dof_enabled;
  }
  
  /**
   * Set DoF focus distance (only applies if DoF is enabled)
   */
  public setLensFocusDistance(value: number): void {
    this.lensConfig.dof_focus_distance = value;
    if (this.lensRenderingPipeline && this.lensConfig.dof_enabled) {
      this.lensRenderingPipeline.setFocusDistance(value);
    }
  }
  
  /**
   * Set DoF aperture (blur amount)
   * Note: Higher values = MORE blur (miniature/tilt-shift effect)
   * Use low values (0.01-0.2) for subtle background blur
   */
  public setLensAperture(value: number): void {
    this.lensConfig.dof_aperture = value;
    if (this.lensRenderingPipeline && this.lensConfig.dof_enabled) {
      this.lensRenderingPipeline.setAperture(value);
    }
  }
  
  /**
   * Set auto-focus target getter (call this from main.ts with player position getter)
   */
  public setAutoFocusTarget(getter: () => Vector3 | null): void {
    this.autoFocusTargetGetter = getter;
  }
  
  /**
   * Enable/disable auto-focus on player
   */
  public setDofAutoFocus(enabled: boolean): void {
    this.lensConfig.dof_auto_focus = enabled;
    logger.debug.log(`🔭 DoF auto-focus ${enabled ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Get current auto-focus state
   */
  public isDofAutoFocusEnabled(): boolean {
    return this.lensConfig.dof_auto_focus;
  }
  
  /**
   * Update auto-focus distance (call this each frame from game loop)
   */
  public updateAutoFocus(): void {
    if (!this.lensConfig.dof_auto_focus) return;
    if (!this.lensRenderingPipeline) return;
    if (!this.lensConfig.dof_enabled) return;
    if (!this.autoFocusTargetGetter) return;
    
    const targetPos = this.autoFocusTargetGetter();
    if (!targetPos) return;
    
    const cameraPos = this.camera.position;
    const distance = Vector3.Distance(cameraPos, targetPos);
    
    // Only update if distance changed significantly (avoid jitter)
    const threshold = 0.5;
    if (Math.abs(distance - this.lensConfig.dof_focus_distance) > threshold) {
      this.lensConfig.dof_focus_distance = distance;
      this.lensRenderingPipeline.setFocusDistance(distance);
    }
  }
  
  /**
   * Set edge blur amount
   */
  public setLensEdgeBlur(value: number): void {
    this.lensConfig.edge_blur = value;
    if (this.lensRenderingPipeline) {
      this.lensRenderingPipeline.setEdgeBlur(value);
    }
  }
  
  /**
   * Set grain amount
   */
  public setLensGrainAmount(value: number): void {
    this.lensConfig.grain_amount = value;
    if (this.lensRenderingPipeline) {
      this.lensRenderingPipeline.setGrainAmount(value);
    }
  }
  
  /**
   * Dispose lens effect pipeline
   */
  public disposeLensEffect(): void {
    if (this.lensRenderingPipeline) {
      this.lensRenderingPipeline.dispose();
      this.lensRenderingPipeline = null;
      this.lensEffectEnabled = false;
    }
  }

  public dispose(): void {
    if (this.pixelationPost) {
      this.pixelationPost.dispose(this.camera);
      this.pixelationPost = null;
    }

    if (this.chromaticAberrationPost) {
      this.chromaticAberrationPost.dispose(this.camera);
      this.chromaticAberrationPost = null;
      this.chromaticAberrationAttached = false;
    }
    
    this.godRaysPosts.forEach(post => {
      post.dispose(this.camera);
    });
    this.godRaysPosts = [];
    this.godRaysMeshes = [];
    this.godRaysMeshToPost.clear();
    
    this.disposeLensEffect();
  }
}
